import React, { useEffect, useState } from 'react';

import { LoginModal, Props as LoginModalProps } from './LoginModal';
import {
  CtaLayout,
  HeaderVariant,
  LoginModalAutotestHooks,
  ModalPage,
  PartialClickTrackingOptions,
  PartialConfigurableText,
  RegistrationParams,
} from './types';
import Cookies from 'js-cookie';
import routes from 'js/core_pre_webpack/routes';
import { AutotestHooksProvider } from './autotestHooks';
import {
  resolveLoginModalAutotest,
  useAutotestPageInit,
} from './autotest/harness';

export type LoginModalEventDetail = {
  postLoginRedirectUrl?: string;
  forcePostLoginRedirectUrl?: boolean;
  clickTrackingContext?: PartialClickTrackingOptions;
  configuredText?: PartialConfigurableText;
  registrationParams: RegistrationParams;
  initialPage?: ModalPage;
  // Structural sockets, chosen by the opener (default to the classic modal).
  headerVariant?: HeaderVariant;
  ctaLayout?: CtaLayout;
  // Work context, rendered by the yourPaperCard header variant.
  workTitle?: string;
  authorName?: string;
  workThumbnailUrl?: string;
  workYear?: number;
  workViewCount?: number;
};

// # Usage
//
// It isn't necessary to use openLoginModal directly. The LoginModalManager will listen for
// the 'open-login-modal' event and open the modal when it is triggered. Example:
//
// document.dispatchEvent(new CustomEvent('open-login-modal', {
//   detail: {
//     postLoginRedirectUrl: '/dashboard',
//     configuredText: {
//       buttonsPage: {
//         header: 'Custom welcome message'
//       }
//     },
//     registrationParams: {
//       user_source: 'header_button'
//     }
//   }
// }));
//
export const openLoginModal = (detail: LoginModalEventDetail) => {
  document.dispatchEvent(new CustomEvent('open-login-modal', { detail }));
};

export const LoginModalManager: React.FC = () => {
  const [modalProps, setModalProps] = useState<LoginModalProps>({
    open: false,
    registrationParams: {
      user_source: 'unknown', // user_source is required in event so this should never actually be sent
    },
  });
  const [autotest, setAutotest] = useState<LoginModalAutotestHooks | undefined>(
    undefined,
  );

  useAutotestPageInit();

  useEffect(() => {
    const handleOpenLoginModal = (
      event: CustomEventInit<LoginModalEventDetail>,
    ) => {
      const eventDetail = event.detail;
      if (!eventDetail) {
        throw new Error('No detail provided to open-login-modal event');
      }

      if (
        !document.querySelector<HTMLMetaElement>("[name='csrf-token']") &&
        !Cookies.get('masked_csrf_token')
      ) {
        window.location.href = routes.login_path({
          post_login_redirect_url: encodeURIComponent(window.location.href),
          modal_fallback: 'true',
        });
      } else {
        const { detail, autotest: resolvedHooks } =
          resolveLoginModalAutotest(eventDetail);
        setAutotest(resolvedHooks);
        setModalProps(() => ({
          open: true,
          postLoginRedirectUrl: detail.postLoginRedirectUrl,
          forcePostLoginRedirectUrl: detail.forcePostLoginRedirectUrl,
          clickTrackingContext: detail.clickTrackingContext,
          configuredText: detail.configuredText,
          registrationParams: detail.registrationParams,
          initialPage: detail.initialPage,
          headerVariant: detail.headerVariant,
          ctaLayout: detail.ctaLayout,
          workTitle: detail.workTitle,
          authorName: detail.authorName,
          workThumbnailUrl: detail.workThumbnailUrl,
          workYear: detail.workYear,
          workViewCount: detail.workViewCount,
        }));
      }
    };

    document.addEventListener('open-login-modal', handleOpenLoginModal);

    return () => {
      document.removeEventListener('open-login-modal', handleOpenLoginModal);
    };
  }, []);

  const handleClose = () => {
    autotest?.onClose?.();
    setModalProps((prev) => ({
      ...prev,
      open: false,
    }));
  };

  return (
    <AutotestHooksProvider value={autotest}>
      <LoginModal {...modalProps} onClose={handleClose} />
    </AutotestHooksProvider>
  );
};
